import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { moneyByCurrency } from "@talentkontor/domain";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { EmptyState, ErrorState, Loading } from "../../../components/States";
import { PillView, Toolbar } from "../../../components/ui";
import { date, money, numberLocale } from "../../../lib/format";
import { useFormularDaten, useHeute } from "../kooperationen/hooks";
import { useAnfragen } from "./hooks";
import { TarifHinweis, useModul } from "../../../components/Tarif";
import { FILTERS, STAGE_PILL, baueZeilen, boardSpalten, offenerWert, passtAuswahl, passtFilter, zaehle, type AnfrageFilter, type AnfrageZeile } from "./model";
import "../kooperationen/kooperationen.css";
import "./anfragen.css";
import { t, ta } from "../../../lib/sprache";
import { ListenVoreinstellungen } from "../einstellungen/Voreinstellungen";
import { PdfDesignLink } from "../../../components/PdfDesignLink";
import { useNurLesen } from "../../../app/nurLesen";

/**
 * Anfragen (0072): Pipeline vor der Kooperation als Liste und als Board je Stand, Filter nach Creator, Marke und verantwortlicher
 * Person, Wert der offenen Angebote. Anfragen zählen nirgends als Kooperation; erst die Zusage legt eine an.
 */
export function Anfragen() {
  const ws = useStaffWorkspace();
  const q = useAnfragen();
  const fd = useFormularDaten();
  // Tarifstaffel (0099): neue Anfragen erst mit „Anfragen und Angebote“; bestehende bleiben lesbar und bearbeitbar
  const neuGesperrt = useModul("module_pipeline").gesperrt;
  // Nur-Lesen (Praxistest 04.11.2026): Anlegen entfällt ausdrücklich, nicht nur über die Regel in portal.css
  const nurLesen = useNurLesen();
  const neuKnopf = (label: string) => (neuGesperrt
    ? <button className="primary" type="button" disabled aria-describedby="anfrage-tarif">{label}</button>
    : <Link className="btn primary" to="/agentur/anfragen/neu">{label}</Link>);
  const today = useHeute();
  const amounts = ws.can("view_amounts");
  const [params, setParams] = useSearchParams();
  const board = params.get("ansicht") === "board";
  const filter = (FILTERS.some((f) => f.key === params.get("filter")) ? params.get("filter") : "offen") as AnfrageFilter;
  const auswahl = { creator: params.get("creator") ?? "", marke: params.get("marke") ?? "", manager: params.get("manager") ?? "", suche: params.get("q") ?? "" };
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const zeilen = useMemo(() => (q.data && fd.data ? baueZeilen(q.data, { today, talents: fd.data.talents, brands: fd.data.brands }) : []), [q.data, fd.data, today]);

  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (fd.error) return <ErrorState error={fd.error} retry={() => void fd.refetch()} />;
  if (q.isPending || fd.isPending || !q.data || !fd.data) return <Loading />;
  const gesucht = zeilen.filter((z) => passtAuswahl(z, auswahl));
  const counts = zaehle(gesucht);
  const sichtbar = gesucht.filter((z) => passtFilter(z, filter));
  const offen = gesucht.filter((z) => z.offen);
  const wert = offenerWert(gesucht);
  const nachfassen = gesucht.filter((z) => z.nachfassen).length;
  const ansicht = (b: boolean) => {
    const next = new URLSearchParams(params);
    if (b) next.set("ansicht", "board");
    else next.delete("ansicht");
    setParams(next, { replace: true });
  };

  return (
    <main id="main" className="content tk-koop tk-anfragen">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          <h1>{t("Anfragen", "Inquiries")}</h1>
          <p>{t("Vor der Kooperation: Anfragen, Angebote und Verhandlungen. Sie zählen nicht als Kooperation und nicht als Umsatz.", "Before the deal: inquiries, offers and negotiations. They do not count as deals or revenue.")}</p>
        </div>
        <div className="row tk-anfrage-ansicht tk-kopf-aktionen">
          {/* Paket 2: Liste | Board als zusammenhängender Umschalter wie bei den Kooperationen (Paket 1) */}
          <div className="tk-ansicht" role="group" aria-label={t("Ansicht", "View")}>
            <button aria-pressed={!board} onClick={() => ansicht(false)}>{t("Liste", "List")}</button>
            <button aria-pressed={board} onClick={() => ansicht(true)}>Board</button>
          </div>
          {!nurLesen && neuKnopf(t("Neue Anfrage", "New inquiry"))}
          <PdfDesignLink art="offer" />
          {/* IA-1 Schritt 3: „⋯ → Voreinstellungen“ */}
          <ListenVoreinstellungen modul="anfragen" />
        </div>
      </div>
      {neuGesperrt && <TarifHinweis modul="module_pipeline" id="anfrage-tarif" was={t("Neue Anfragen und Angebote", "New inquiries and offers")} />}

      {zeilen.length === 0 ? (
        <EmptyState title={t("Noch keine Anfrage.", "No inquiry yet.")} text={t("Hält eine Marke nach Creatorn Ausschau, lege hier die Anfrage an, schlage Creator vor und schicke ein Angebot. Erst die Zusage wird zur Kooperation.", "When a brand is looking for creators, create the inquiry here, propose creators and send an offer. Only the acceptance becomes a deal.")}
          action={neuKnopf(t("Anfrage anlegen", "Create inquiry"))} />
      ) : (
        <>
          <div className="tk-beitraege-kacheln" aria-label={t("Kennzahlen der Anfragen", "Inquiry metrics")}>
            <div className="panel stack"><span className="eyebrow">{t("Offene Anfragen", "Open inquiries")}</span><span className="metric num">{offen.length}</span></div>
            {amounts && <div className="panel stack"><span className="eyebrow">{t("Wert offener Angebote", "Value of open offers")}</span><span className="metric num">{moneyByCurrency(wert, numberLocale())}</span></div>}
            <div className="panel stack"><span className="eyebrow">{t("Nachfassen fällig", "Follow-up due")}</span><span className="metric num">{nachfassen}</span></div>
            <div className="panel stack"><span className="eyebrow">{t("Zusagen gesamt", "Total acceptances")}</span><span className="metric num">{gesucht.filter((z) => z.inquiry.stage === "won").length}</span></div>
          </div>
          <Toolbar
            search={auswahl.suche}
            onSearch={(v) => setParam("q", v)}
            label={t("Anfragen durchsuchen", "Search inquiries")}
            placeholder={t("Titel, Marke, Creator, Angebotsnummer …", "Title, brand, creator, offer number …")}
            filters={board ? undefined : FILTERS.map((f) => ({ ...f, count: counts[f.key] }))}
            filter={filter}
            onFilter={(f) => setParam("filter", f === "offen" ? "" : f)}
          >
            <select hidden={ws.isCreator} aria-label={t("Nach Creator filtern", "Filter by creator")} value={auswahl.creator} onChange={(e) => setParam("creator", e.target.value)}>
              <option value="">{t("Alle Creator", "All creators")}</option>
              {fd.data.talents.filter((t) => t.status !== "archived").map((t) => <option key={t.id} value={t.id}>{t.display_name?.trim() || `${t.first_name} ${t.last_name ?? ""}`.trim()}</option>)}
            </select>
            <select aria-label={t("Nach Marke filtern", "Filter by brand")} value={auswahl.marke} onChange={(e) => setParam("marke", e.target.value)}>
              <option value="">{t("Alle Marken", "All brands")}</option>
              {fd.data.brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select aria-label={t("Nach verantwortlicher Person filtern", "Filter by responsible person")} value={auswahl.manager} onChange={(e) => setParam("manager", e.target.value)}>
              <option value="">{t("Alle Verantwortlichen", "All responsible people")}</option>
              {q.data.members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              <option value="ohne">{t("Ohne Verantwortliche", "Without responsible person")}</option>
            </select>
          </Toolbar>
          {board ? <AnfragenBoard zeilen={gesucht} amounts={amounts} /> : <AnfragenListe zeilen={sichtbar} amounts={amounts} reset={() => setParams(new URLSearchParams(), { replace: true })} />}
          <p className="source-note">
            {t("Stand: Anfrage → Angebot gesendet → Verhandlung → Zusage (wird zur Kooperation), sonst Abgesagt (wir sagen ab) oder Verloren (die Marke sagt ab).", "Status: inquiry → offer sent → negotiation → accepted (becomes a deal), otherwise declined (we decline) or lost (the brand declines).")}{amounts ? t(" Wert: Gesamtsumme des zuletzt gesendeten bzw. festgeschriebenen Angebots.", " Value: total of the most recently sent or finalised offer.") : t(" Beträge siehst du mit dem Recht „Beträge sehen“.", " You see amounts with the “View amounts” permission.")}
          </p>
        </>
      )}
      {api.mode === "demo" && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictional.")}</p>}
    </main>
  );
}

function AnfragenListe({ zeilen, amounts, reset }: { zeilen: AnfrageZeile[]; amounts: boolean; reset: () => void }) {
  if (!zeilen.length) {
    return (
      <div className="panel stack">
        <p>{t("Keine passenden Anfragen. Passe die Suche oder die Filter an.", "No matching inquiries. Adjust the search or the filters.")}</p>
        <div className="row"><button onClick={reset}>{t("Filter zurücksetzen", "Reset filters")}</button></div>
      </div>
    );
  }
  return (
    <div className="tablewrap tk-cardtable tk-koop-karten tk-koop-liste tk-anfragen-liste tk-sticky-end">
      <table>
        <caption className="sr-only">{t("Anfragen", "Inquiries")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("Anfrage", "Inquiry")}</th><th scope="col">Creator</th><th scope="col">{t("Stand", "Status")}</th><th scope="col" className="tk-anf-c-eingang">{t("Eingang", "Received")}</th><th scope="col" className="tk-anf-c-verantwortlich">{t("Verantwortlich", "Responsible")}</th>
            {amounts && <th scope="col" className="money">{t("Angebot", "Offer")}</th>}
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z) => (
            <tr key={z.inquiry.id}>
              <td className="tk-koop-kopf tk-koop-zelle">
                <Link to={`/agentur/anfragen/${z.inquiry.id}`} title={`${z.brandName} · ${z.inquiry.title}`}><strong>{z.brandName} · {z.inquiry.title}</strong></Link>
                {z.nachfassen && <small className="tk-koop-ueberfaellig">{t("Nachfassen fällig", "Follow-up due")}</small>}
                {/* N-1: eingeklappte Spalten „Eingang“ und „Verantwortlich“ (schmale Liste bei 1024 px) */}
                <small className="tk-koop-alt">{t("Eingang ", "Received ")}{date(z.inquiry.received_on)}{z.managerName ? ` · ${z.managerName}` : ""}</small>
              </td>
              <td data-label="Creator" className="tk-koop-zelle"><span className="tk-anfrage-creator" title={z.creator.join(", ") || undefined}>{z.creator.length ? z.creator.join(", ") : "–"}</span>{z.candidates.length > 1 && <small>{t("Casting · ", "Casting · ")}{z.candidates.length} Creator</small>}</td>
              <td data-label={t("Stand", "Status")} className="tk-koop-status"><PillView pill={STAGE_PILL[z.inquiry.stage]} />{z.inquiry.close_reason && <small>{z.inquiry.close_reason}</small>}</td>
              <td data-label={t("Eingang", "Received")} className="tk-anf-c-eingang">{date(z.inquiry.received_on)}</td>
              <td data-label={t("Verantwortlich", "Responsible")} className="tk-anf-c-verantwortlich">{z.managerName ?? "–"}</td>
              {amounts && (
                <td data-label={t("Angebot", "Offer")} className="num">
                  {z.angebot ? money(z.wertCents, z.angebot.currency) : "–"}
                  {z.angebot?.number && <small className="tk-nowrap">{z.angebot.number}</small>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnfragenBoard({ zeilen, amounts }: { zeilen: AnfrageZeile[]; amounts: boolean }) {
  const spalten = boardSpalten(zeilen);
  return (
    <div className="tk-koop-board" role="region" aria-label={t("Board nach Stand", "Board by status")} tabIndex={0}>
      {spalten.map((s) => (
        <section key={s.stage} className="panel stack tk-koop-spalte" aria-label={`${s.pill.label}, ${s.zeilen.length}`}>
          <h3>{s.pill.label} · {s.zeilen.length}</h3>
          {amounts && Object.keys(s.wert).length > 0 && <small className="num">{moneyByCurrency(s.wert, numberLocale())}</small>}
          {s.zeilen.length === 0 && <p className="muted">{t("Keine Anfrage in diesem Stand.", "No inquiry in this status.")}</p>}
          {s.zeilen.map((z) => (
            <Link key={z.inquiry.id} className="panel stack tk-koop-karte" to={`/agentur/anfragen/${z.inquiry.id}`}>
              <strong>{z.brandName}</strong>
              <p>{z.inquiry.title}</p>
              <small>{z.creator.length ? z.creator.join(", ") : t("Noch kein Creator", "No creator yet")}</small>
              {amounts && z.angebot && <span className="num">{money(z.wertCents, z.angebot.currency)}{z.angebot.number ? ` · ${z.angebot.number}` : ""}</span>}
              <small>{t("Eingang ", "Received ")}{date(z.inquiry.received_on)}{z.managerName ? ` · ${z.managerName}` : ""}</small>
              {z.nachfassen && <small className="tk-koop-ueberfaellig">{t("Nachfassen fällig", "Follow-up due")}</small>}
            </Link>
          ))}
        </section>
      ))}
    </div>
  );
}
